import { Injectable, signal, OnDestroy, computed, inject } from '@angular/core';
import {
  BossAuthAccessTokens,
  BossAuthCredentials,
} from '../../auth/schemas/boss-auth.schema';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FireAuthService } from './fire-auth.service';
import { map, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BossAuthTokenService } from './boss-auth-token.service';
import { logout } from '@directus/sdk';
import { directusAuthInstance } from '../libs/directus';

@Injectable({
  providedIn: 'root',
})
export class BossAuthService implements OnDestroy {
  private fireAuthService = inject(FireAuthService);
  private _destroy$ = new Subject<void>();
  bossAPIClientAuthSession = directusAuthInstance;

  fireAuthToken = signal<string>('');

  httpOptions = computed(() => {
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.fireAuthService.fireAuthToken()}`,
        'Content-Type': 'application/json',
      }),
    };
  });

  constructor(
    private http: HttpClient,
    private bossAuthTokenService: BossAuthTokenService,
  ) {
    // effect(() => {
    //   if (this.fireAuthService.authStateMessage() === 'SIGNED_OUT') {
    //     console.log('Signed out triggered');
    //     this.fireAuthToken.set('');
    //     this.signOut();
    //   }
    // });
  }

  signUpWithEmailPassword(credentials: BossAuthCredentials) {
    return this.http
      .post(`${environment.authUrl}/signup`, credentials, this.httpOptions())
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          this.bossAuthTokenService.set(response as BossAuthAccessTokens);
          return response;
        }),
      );
  }

  authWithSSO(credentials: BossAuthCredentials) {
    return this.http
      .post(`${environment.authUrl}/sso`, credentials, this.httpOptions())
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          this.bossAuthTokenService.set(response as BossAuthAccessTokens);
          return response;
        }),
      );
  }

  loginWithEmailPassword(credentials: BossAuthCredentials) {
    return this.http
      .post(`${environment.authUrl}/login`, credentials, this.httpOptions())
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          this.bossAuthTokenService.set(response as BossAuthAccessTokens);
          return response;
        }),
      );
  }

  resetPassword(credentials: BossAuthCredentials) {
    return this.http
      .post(
        `${environment.authUrl}/reset-password`,
        credentials,
        this.httpOptions(),
      )
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          // this.bossAuthTokenService.set(response as BossAuthAccessTokens);
          return response;
        }),
      );
  }

  phoneAuth(credentials: BossAuthCredentials) {
    return this.http
      .post(`${environment.authUrl}/phone`, credentials, this.httpOptions())
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          // this.bossAuthTokenService.set(response as BossAuthAccessTokens);
          return response;
        }),
      );
  }

  verifyOTP(credentials: BossAuthCredentials) {
    return this.http
      .post(
        `${environment.authUrl}/verify-otp`,
        credentials,
        this.httpOptions(),
      )
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          this.bossAuthTokenService.set(response as BossAuthAccessTokens);
          return response;
        }),
      );
  }

  // async getUser() {
  //   return await this.requestsService.handleRequest(() =>
  //     this.bossAPIClientAuthSession.request(readItems('user')),
  //   );
  // }

  // async handleBossAuthError(error: any) {
  //   try {
  //     return await this.requestsService.handleRequestErrors(error);
  //   } catch (error) {}
  // }

  async handleBossAuthError(response: any) {
    const { errors } = response;
    const code =
      errors?.[0]?.extensions?.code || response.code || response.error.code;
    // console.log('ERROR RESPONSE:', response);
    // console.log('ERROR CODE:', code);
    switch (code) {
      case 'FORBIDDEN':
        // return this.initializeSession();
        await this.signOut();
        return this.fireAuthService.signOut();
      case 'TOKEN_EXPIRED':
        // try {
        //   await this.refreshTokens();
        //   return 'REFRESHED';
        // } catch (refreshError) {
        //   console.log('Token refresh failed', refreshError);
        //   await this.fireAuthService.signOut();
        //   return 'TOKEN_EXPIRED';
        // }
        if (this.bossAuthTokenService.get()) {
          await this.signOut();
        }
        await this.fireAuthService.signOut();
        return 'Your session has expired.';
      case 'BOSS_AUTH_INVALID_TOKEN':
        return 'Sorry, your session has expired.';
      case 'UNAUTHORIZED':
        return 'You are not authorized to perform this action.';
      case 'RECAPTCHA_FAILED':
        location.reload();
        return 'Something went wrong.';
      case 'FAILED_TO_SEND_MESSAGE':
        return 'An error occurred while sending the message. Please retry.';
      case 'INVALID_CREDENTIALS':
        return 'Invalid credentials. Please check your email and password or reset your password.';
      case 'INVALID_USER':
        return 'Invalid user.';
      case 'RECORD_NOT_UNIQUE':
        return 'Record not unique.';
      case undefined:
        return errors?.message || response.error.message;
      default:
        return this.fireAuthService.authErrorHandler(response);
    }
  }

  async signOut() {
    try {
      const tokens =
        this.bossAuthTokenService.get() ??
        this.bossAuthTokenService.authAccessTokens();

      // console.log('tokens', tokens);
      await this.bossAPIClientAuthSession.request(
        logout(tokens?.refresh_token, 'json'),
      );

      // console.log('Signed out response', response);
      this.bossAuthTokenService.destroy();
      // this.signalEventsService.signedOut.set(true);

      return await this.fireAuthService.signOut();
    } catch (error) {
      this.bossAuthTokenService.destroy();
      // console.log('error in signOut', error);
      // console.log('TOkens', this.bossAuthTokenService.get());
      return await this.fireAuthService.signOut();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
